<i18n>
{
  "en": {
    "noMenu": "Error loading menu",
    "saveMenuEngineeringSuccess": "Menu engineering recommendations confirmed successfully.",
    "saveMenuEngineeringError": "Error confirming menu engineering recommendations.",
    "confirmTitle": "Confirming changes for menu",
    "confirmDescription": "Changes will be saved for all groups",
    "confirm": "Confirm",
    "selectLanguage": "Select the language to display"
  },
  "es": {
    "noMenu": "Error cargando menú",
    "saveMenuEngineeringSuccess": "Se han confirmado las recomendaciones de menu engineering correctamente.",
    "saveMenuEngineeringError": "Error confirmando las recomendaciones de menu engineering.",
    "confirmTitle": "Confirmando cambios para el menú",
    "confirmDescription": "Se guardarán los cambios para todas las agrupaciones",
    "confirm": "Confirmar",
    "selectLanguage":  "Seleccionar el lenguaje a mostrar"
  }
}
</i18n>
<template>
  <v-container>
    <v-row class="mb-3 align-center">
      <v-col class="d-flex mt-lg-4 mt-md-4 flex-column flex-md-row" cols="12" sm="6">
        <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1" v-if="menuRecommendation">
          {{ menuRecommendation.name }}
        </div>
        <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1" v-if="selectedPeriod">
          {{ selectedPeriod.name }}
        </div>
      </v-col>
      <v-col class="d-flex mt-lg-4 mt-md-4 justify-md-space-between align-center" cols="12" sm="6">
        <div class="mr-3">
          <menu-engineering-group-info-button/>
        </div>
        <div>
          <v-select v-model="language"
                    :items="pointOfSaleLanguages"
                    item-text="name"
                    item-value="alternative"
                    :label="$t('selectLanguage')"
                    hide-details
                    height="42"
          />
        </div>
      </v-col>
    </v-row>
    <v-list class="mt-4 mb-16" v-if="menuRecommendation && !isLoading">
      <section-card v-for="section in menuRecommendation.sections" :key="section.guid" class="mb-3"
                    :section="section" :is-recommendation-editable="isRecommendationEditable"
                    @group-click="onGroupClick" @confirmation-changed="onConfirmationChanged"/>
    </v-list>
    <dynameat-skeleton v-if="isLoading" skeleton-type="manualConfigList" :container="false"/>
    <dynameat-confirmation-modal :visible="confirmModalVisible"
                                 :title="$t('confirmTitle')"
                                 :description="$t('confirmDescription')"
                                 :confirm-text="$t('confirm')"
                                 :is-loading="isLoading"
                                 @confirm="onSave"
                                 @cancel="onToggleModal"/>
    <dynameat-confirmation-buttons @cancel="onCancel" @confirm="onToggleModal"
                                   :confirm-enabled="isRecommendationEditable"/>
  </v-container>
</template>
<script>
/* eslint-disable object-property-newline */

import Vue from 'vue';
import Component from 'vue-class-component';
import {
  Components, MapActions, MapGetters, Mixins
} from '@core/helpers/decorators';
import DynameatDropdown from '@dynameat/components/DynameatDropdown';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { periodMixins } from '@pointOfSale/mixins';
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import VueRouter from 'vue-router';
import { routeOutConfirmationMixin } from '@core/mixins';
import { Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';
import { sentryLogException } from '@core/helpers/sentryLogger';
import SectionCard from '../components/SectionCard.vue';
import MenuEngineeringGroupInfoButton from '../components/MenuEngineeringGroupInfoButton.vue';
import EngineeringProfitabilityChart from '../components/EngineeringProfitabilityChart.vue';

const { isNavigationFailure, NavigationFailureType } = VueRouter;

Component.registerHooks(['beforeRouteEnter']);
@Component
@Components({
  DynameatDropdown, SectionCard, DynameatConfirmationButtons, DynameatConfirmationModal,
  DynameatSkeleton, MenuEngineeringGroupInfoButton, EngineeringProfitabilityChart
})
@MapGetters(['menuRecommendation', 'menuRecommendationList', 'isLoading',
  'getSectionProfitability', 'pointOfSaleLanguages', 'optimizerLanguage'])
@MapActions(['menuRecommendationRequest', 'saveMenuRecommendationRequest', 'setEngineeringProfitabilityLoading',
  'addNotification', 'setLoading', 'sectionProfitabilityRequest', 'menuProfitabilityRequest',
  'removeNotificationById', 'menuRecommendationListRequest', 'selectPriceLanguage'])
@Mixins([periodMixins, routeOutConfirmationMixin])
export default class MenuRecommendation extends Vue {
  confirmModalVisible = false;

  // Needed to have a registry of profitability sections to calculate. Is cleaned after each request
  sectionGuidsModified = {}

  language = 'alt1'

  get isRecommendationEditable() {
    const periodEditable = this.selectedPeriod?.isMenuEditable();
    const recValidated = this.menuRecommendationList.find((rec) => rec.guid === this.$route.params.menuRecommendationGuid)?.isRecommendationValidated;
    return periodEditable || (!periodEditable && !recValidated);
  }

  created() {
    if (!this.menuRecommendation) this.getMenuRecommendation();
    this.language = this.optimizerLanguage;
  }

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === 'menu-recommendation-list') vm.getMenuRecommendation();
    });
  }

  @Watch('menuRecommendation', { deep: true, immediate: true })
  onMenuChanged() {
    this.allowRouteExitWithoutConfirmation = this.menuRecommendation && !this.menuRecommendation.hasChange();
  }

  async getMenuRecommendation() {
    try {
      await this.setLoading(1);
      await this.menuRecommendationRequest(this.$route.params.menuRecommendationGuid);
      if (this.menuRecommendationList.length === 0) {
        await this.menuRecommendationListRequest(this.$route.params.periodGuid);
      }
      await this.setLoading(-1);
    } catch (err) {
      sentryLogException(err, 'Error getting menu recommendation');
      await this.setLoading(-1);
      this.addNotification(createErrorNotification(this.$t('engineeringLoadingError'), null, 5000));
      this.$router.push({ name: 'optimizer', params: { periodGuid: this.$route.params.periodGuid } }).catch((err) => {
        if (!isNavigationFailure(err, NavigationFailureType.aborted)) {
          Promise.reject(err);
        }
      });
    }
  }

  onToggleModal() {
    this.confirmModalVisible = !this.confirmModalVisible;
  }

  getSectionProfitabilityValue(section) {
    const sectionProfitability = this.getSectionProfitability(this.menuRecommendation.guid, section.guid);
    if (sectionProfitability) {
      if (sectionProfitability.profitability > 0.25) return 5;
      if (sectionProfitability.profitability > 0.20) return 4;
      if (sectionProfitability.profitability > 0.15) return 3;
      if (sectionProfitability.profitability > 0.10) return 2;
      if (sectionProfitability.profitability > 0.0) return 1;
    }
    return 0;
  }

  onGroupClick({ section, groupCodeName }) {
    this.allowRouteExitWithoutConfirmation = true;
    this.$router.push({
      name: 'menu-recommendation-detail',
      params: { section, groupCodeName, isRecommendationEditable: this.isRecommendationEditable }
    });
  }

  async onConfirmationChanged(sectionGuid) {
    // Keep track of last changes in section for profitability
    this.sectionGuidsModified[sectionGuid] = !this.sectionGuidsModified[sectionGuid];
    this.debouncedSectionProfitabilityRequest();
  }

  debouncedSectionProfitabilityRequest = debounce(this.reloadProfitabilityBySectionRequest, 1500)

  async reloadProfitabilityBySectionRequest() {
    const { periodGuid } = this.$route.params;
    const menuGuid = this.menuRecommendation.guid;
    const sectionGuids = [];
    try {
      await this.setEngineeringProfitabilityLoading(1);
      Object.entries(this.sectionGuidsModified).forEach(([sectionGuid, modified]) => {
        if (modified) sectionGuids.push(sectionGuid);
      });
      this.sectionGuidsModified = {}; // Clean data
      if (sectionGuids.length > 0) {
        // If there are section changes to recalculate
        await this.sectionProfitabilityRequest({ periodGuid, menuGuid, sectionGuids });
      }
    } catch (err) {
      sentryLogException(err, `Error reloading menu engineering profitability for period ${periodGuid}, menu ${menuGuid} and sections ${sectionGuids}`);
    } finally {
      await this.setEngineeringProfitabilityLoading(-1);
    }
  }

  async onSave() {
    try {
      await this.setLoading(1);
      const { periodGuid, menuRecommendationGuid } = this.$route.params;
      await this.saveMenuRecommendationRequest({ periodGuid, menuRecommendationGuid });
      if (this.menuRecommendationList.every((el) => el.isRecommendationValidated)) {
        this.removeNotificationById(`menu${this.$route.params.periodGuid}`);
      }
      this.addNotification(createSuccessNotification(this.$t('saveMenuEngineeringSuccess')));
    } catch (err) {
      sentryLogException(err, 'Error confirming menu engineering recommendations');
      this.addNotification(createErrorNotification(this.$t('saveMenuEngineeringError')));
    } finally {
      await this.setLoading(-1);
      this.allowRouteExitWithoutConfirmation = true;
      this.$router.push({ name: 'menu-recommendation-list', params: { periodGuid: this.$route.params.periodGuid } });
    }
  }

  onCancel() {
    this.$router.push({ name: 'menu-recommendation-list' }, this.resetProfitability)
      ?.catch((e) => {
        if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
          Promise.reject(e);
        }
      });
  }

  async resetProfitability() {
    const { params: { periodGuid } } = this.$route;
    const menuGuid = this.menuRecommendation.guid;
    try {
      if (this.isRecommendationEditable && this.menuRecommendation.hasChange()) {
        await this.setEngineeringProfitabilityLoading(1);
        await this.menuProfitabilityRequest({ periodGuid, menuGuid });
      }
    } catch (err) {
      sentryLogException(err, `Error cancelling menu changes and getting new profitability for menu "${menuGuid}"`);
    } finally {
      await this.setEngineeringProfitabilityLoading(-1);
    }
  }

  @Watch('language', { immediate: false })
  onLanguageChanged(newLanguage, oldLanguage) {
    if (newLanguage !== oldLanguage) {
      this.selectPriceLanguage(newLanguage);
    }
  }
}

</script>

<style scoped lang="css">
.info-container {
  margin-bottom: -50px;

}

.price-chart {
  margin-top: -15px;
}
</style>
